/**
 * OC-fire "watching" score — the FAIL-LOUD representation of an OC fire's urgent
 * standing (#618 Defect 2). The Track-B escalation index (0..100) carries a freshness
 * stamp, and NOTHING clears it. So a fire scored ≥60 that then goes quiet used to either
 *   (a) red-pin FOREVER on the stale value, or
 *   (b) once the escalator is freshness-gated, SILENTLY drop to the RL2 floor —
 * hiding that we LOST fresh signal on a fire that was escalating.
 *
 * This discriminated union makes an uncomputable / stale score UN-REPRESENTABLE as a
 * benign low number: an OC fire is either a fresh `scored` result or an explicit
 * `degraded` one the panel + queue surface as "watching degraded — last observed N ago".
 * A NULL can no longer type-check its way into a low RL. Pinned by ocFireWatch.lock.test.ts.
 */

/**
 * Track-B escalation score freshness TTL (hours). A score older than this is UNTRUSTED —
 * NOT its last value. 12 h ≫ the fusion cycle interval (~minutes), so in a healthy running
 * system every OC fire is re-scored well within the window and NEVER degrades; a fire
 * `degraded`s ONLY when scoring stops (a worker outage, or the fire left the scan set) —
 * exactly the fail-loud signal we want to surface rather than swallow. The SQL RL mirror
 * (oc-fire-rl.ts) gates the escalation≥60 escalator on the same window.
 */
export const OC_ESCALATION_FRESH_HOURS = 12;

/** The urgent (red-queue) Response Level floor — mirrors the api URGENT_MIN_RESPONSE_LEVEL
 *  (anomaly-query.ts) + the web one (styles/responseLevel.ts). Local copy so shared-types
 *  stays dependency-free; kept at 3 by the same de-flood discipline. */
export const OC_WATCH_URGENT_MIN_RL = 3;

/**
 * #635 — the SINGLE shared "near values" OUTER radius (metres). ONE constant consumed by
 * BOTH the RL ladder (`OC_UNFOUGHT_VALUES_RADIUS_M`, apps/api/routes/oc-fire-rl.ts) AND the
 * Track-B escalation scorer's value-proximity envelope (`VALUE_PROXIMITY_RADIUS_KM`,
 * apps/ingestion/.../reported-fire-escalation.ts). Two surfaces in one COP must not disagree
 * about "near values": before #635 the ladder reached 50 km (unfought/monitored) while the
 * scorer stopped seeing communities at 30 km, so a monitored fire 35 km from a fly-in First
 * Nation produced a RED RL3 queue row from a scorer that had NEVER SEEN THE COMMUNITY. This
 * is the fought fire's 25 km ladder radius's superset (the outer envelope) — 50 km ≈ 2–3
 * boreal burning periods of lead, and the range at which dense smoke closes an airstrip
 * downwind. Kept in metres (the ladder's unit) with a km derivation for the scorer.
 */
export const OC_VALUES_OUTER_RADIUS_M = 50_000;
export const OC_VALUES_OUTER_RADIUS_KM = OC_VALUES_OUTER_RADIUS_M / 1000;

export interface OcFireWatchInputs {
  /** The server-derived OC Response Level (2..4) from the ladder — already
   *  escalation-freshness-gated (a stale escalation≥60 no longer lifts it). null for a
   *  non-OC fire (→ no watch score). This is the number the map disc + queue read. */
  ocFireResponseLevel: number | null;
  /** The Track-B escalation index (0..100), or null when never scored. */
  escalationIndex: number | null;
  /** When the escalation index was last computed (UTC ISO), or null. */
  escalationUpdatedAt: string | null;
  /** Epoch ms "now" for the freshness comparison (inject for determinism / tests). */
  now: number;
}

/**
 * The OC-fire watch score. `scored` = the fire's urgency is computed from FRESH signals
 * (its RL stands on near-values / size / severe-CAP / a fresh escalation). `degraded` =
 * the fire HAD an escalation signal that is now stale AND nothing fresh keeps it urgent —
 * so its urgency is UNKNOWN, not benign; the panel + queue keep watching it. `escalationStale`
 * rides on `scored` so the panel can annotate a stale-but-not-decisive escalation score
 * (a big fire urgent via SIZE whose escalation index is old still reads "watching degraded").
 */
export type OcFireWatchScore =
  | { kind: 'scored'; rl: number; escalationStale: boolean }
  | {
      kind: 'degraded';
      reason: 'stale_escalation';
      lastObservedAt: string | null;
      escalationIndex: number;
    };

/** True when an escalation score is within the freshness TTL. A null/unparseable stamp is
 *  NOT fresh (fail-loud: an absent stamp is treated as stale, never silently trusted). */
export function isEscalationFresh(updatedAt: string | null | undefined, now: number): boolean {
  if (updatedAt == null) return false;
  const t = Date.parse(updatedAt);
  if (!Number.isFinite(t)) return false;
  return now - t <= OC_ESCALATION_FRESH_HOURS * 3600_000;
}

/**
 * Derive the fail-loud OC watch score from a fire's fields. Returns null for a non-OC fire
 * (mirrors oc_fire_response_level being null). See the module header for the scored/degraded
 * contract. Pure + deterministic (inject `now`).
 */
export function deriveOcFireWatchScore(i: OcFireWatchInputs): OcFireWatchScore | null {
  if (i.ocFireResponseLevel == null) return null;
  const hadScore = i.escalationIndex != null;
  const escalationStale = hadScore && !isEscalationFresh(i.escalationUpdatedAt, i.now);
  // Degraded ⟺ the fire HAD an escalation signal that is now stale AND nothing FRESH keeps
  // it urgent (rl below the urgent floor → it would silently fall to the muted RL2). Then
  // we've LOST the signal → surface degraded + keep it on the queue, never a silent low RL.
  if (escalationStale && i.ocFireResponseLevel < OC_WATCH_URGENT_MIN_RL) {
    return {
      kind: 'degraded',
      reason: 'stale_escalation',
      lastObservedAt: i.escalationUpdatedAt,
      escalationIndex: i.escalationIndex as number,
    };
  }
  return { kind: 'scored', rl: i.ocFireResponseLevel, escalationStale };
}
